import React, { FC } from 'react';
import styled from 'styled-components';
import { globalTheme } from '@clds/component-theme';
import { ButtonRoot, TextWrapper } from '@clds/button';
import { CopyButton } from './components/CopyButton';
import { HorizontalToolbar, StyledCode, ellipsisWidth } from './CodeSnippet.styled';
import { CodeSnippetProps, VersionedSnippet, VersionName } from './types';
import { useActiveSnippet, useActiveSnippetVersion, useSortSnippets } from './hooks';
import { SnippetsLanguagesMenu } from './components/SnippetsLanguagesMenu';
import { CodeSnippetLayout } from './components/CodeSnippetLayout';
import { SnippetVersionPicker, StyledText } from './components/SnippetVersionsPicker';

export interface MobileVersionedCodeSnippetProps extends CodeSnippetProps<VersionedSnippet> {
  onLangVersionChange?: (version: VersionName) => void;
  langVersion?: string;
}

export const MobileHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const HorizontalToolbarMobile = styled(HorizontalToolbar)`
  max-width: 70%;
  overflow: hidden;
  > * {
    margin-left: ${globalTheme.spacing.xxs};
  }
  ${StyledText}, ${ButtonRoot}:not(:only-of-type) {
    ${ellipsisWidth};
  }
  ${TextWrapper} {
    overflow: hidden;
  }
`;

export const MobileVersionedCodeSnippet = styled<FC<MobileVersionedCodeSnippetProps>>(
  ({ snippets, activeLanguage, onLanguageChange, langVersion, onLangVersionChange, onCopy, parsedUrl, height, ...rest }) => {
    const sortedSnippets = useSortSnippets<VersionedSnippet>(snippets);
    const activeSnippet = useActiveSnippet(sortedSnippets, activeLanguage);
    const activeSnippetVersion = useActiveSnippetVersion(langVersion, activeSnippet);

    const header = (
      <MobileHeaderWrapper>
        <SnippetsLanguagesMenu snippets={sortedSnippets} onSnippetChange={onLanguageChange} activeSnippet={activeSnippet} size="md" />
        <HorizontalToolbarMobile>
          <SnippetVersionPicker
            activeVersion={activeSnippetVersion?.versionName}
            versions={activeSnippet.versions}
            onVersionChange={onLangVersionChange}
            size="md"
            tone="solid"
          />
          <CopyButton onCopy={onCopy} contentToCopy={activeSnippetVersion.snippet} size="md" />
        </HorizontalToolbarMobile>
      </MobileHeaderWrapper>
    );

    return (
      <CodeSnippetLayout header={header} {...rest}>
        <StyledCode parsedUrl={parsedUrl} syntax={activeSnippetVersion.syntax ?? activeSnippet.syntax} height={height}>
          {activeSnippetVersion.snippet}
        </StyledCode>
      </CodeSnippetLayout>
    );
  }
)``;
